import type { Card, CardPile, Suit } from "./types.js";

/**
 * Feste Suit-Reihenfolge der 4 Foundation-Felder:
 * Index 0 = Kreuz, 1 = Herz, 2 = Pik, 3 = Karo
 */
export const FOUNDATION_SUIT_ORDER: readonly Suit[] = [
  "clubs",
  "hearts",
  "spades",
  "diamonds",
];

// ============================================================
// Hilfsfunktionen
// ============================================================

/** Gibt die oberste Karte eines Stapels zurück (oder undefined wenn leer). */
export function topCard(pile: CardPile): Card | undefined {
  return pile[pile.length - 1];
}

// ============================================================
// Tableau-Regeln
// ============================================================

/**
 * Prüft ob eine Karte auf einen Tableau-Stapel gelegt werden darf.
 *
 * Regeln:
 * - Leere Spalte: nur König (Rank 13)
 * - Sonst: entgegengesetzte Farbe + einen Rang kleiner
 */
export function canPlaceOnTableau(card: Card, targetPile: CardPile): boolean {
  const target = topCard(targetPile);
  if (target === undefined) {
    // Leere Spalte → nur König erlaubt
    return card.rank === 13;
  }
  if (!target.faceUp) return false;
  return card.color !== target.color && card.rank === target.rank - 1;
}

/**
 * Prüft ob eine Sequenz von Karten auf einen Tableau-Stapel gelegt werden darf.
 * Es wird nur die unterste Karte der Sequenz geprüft.
 */
export function canPlaceSequenceOnTableau(
  cards: readonly Card[],
  targetPile: CardPile
): boolean {
  const bottomCard = cards[0];
  if (bottomCard === undefined) return false;
  return canPlaceOnTableau(bottomCard, targetPile);
}

// ============================================================
// Foundation-Regeln
// ============================================================

/**
 * Prüft ob eine Karte auf einen Foundation-Stapel gelegt werden darf.
 *
 * Regeln:
 * - Leerer Stapel: nur Ass der zugeordneten Farbe (designatedSuit)
 * - Sonst: gleiche Farbe + nächster Rang (aufsteigend)
 */
export function canPlaceOnFoundation(
  card: Card,
  foundationPile: CardPile,
  designatedSuit: Suit,
): boolean {
  const top = topCard(foundationPile);
  if (top === undefined) {
    return card.suit === designatedSuit && card.rank === 1;
  }
  return card.suit === top.suit && card.rank === top.rank + 1;
}

// ============================================================
// Sequenz-Validierung (für Tableau-zu-Tableau Moves)
// ============================================================

/**
 * Prüft ob eine Gruppe von Karten eine gültige Sequenz bildet.
 * Gültig = abwechselnde Farben, absteigende Ränge, alle aufgedeckt.
 */
export function isValidSequence(cards: readonly Card[]): boolean {
  if (cards.length === 0) return false;
  for (let i = 1; i < cards.length; i++) {
    const upper = cards[i - 1];
    const lower = cards[i];
    if (upper === undefined || lower === undefined) return false;
    if (!upper.faceUp || !lower.faceUp) return false;
    if (upper.color === lower.color) return false;
    if (lower.rank !== upper.rank - 1) return false;
  }
  return true;
}
